import type { RelayControlRecoverySummary } from "@cmmd-forge/client-runtime";
import type { CmmdDeviceRegistryDevice, EnvironmentId } from "@cmmd-forge/contracts";

import type { EnvironmentKind } from "./environmentKind";
import type { ProjectRuntimeControl } from "./ProjectRuntimeControls";
import type { RuntimeGroupStatus } from "./RuntimeGroupStatusIndicator";

interface RuntimeControlSource {
  readonly environmentId: EnvironmentId | null;
  readonly device: CmmdDeviceRegistryDevice | null;
  readonly label: string;
  readonly kind: EnvironmentKind;
  readonly status?: RuntimeGroupStatus | undefined;
}

interface BuildProjectRuntimeControlsInput {
  readonly sources: readonly RuntimeControlSource[];
  readonly relayRecoveryByRuntimeId: Readonly<
    Record<string, RelayControlRecoverySummary | undefined>
  >;
  readonly disconnectingDeviceId: number | null;
  readonly disconnectDevice: (
    device: CmmdDeviceRegistryDevice,
    environmentId: EnvironmentId,
  ) => void | Promise<void>;
  readonly acknowledgeRelayRecovery: (environmentId: EnvironmentId) => void;
}

export function buildProjectRuntimeControls(
  input: BuildProjectRuntimeControlsInput,
): readonly ProjectRuntimeControl[] {
  return input.sources
    .map((source) => buildProjectRuntimeControl(source, input))
    .filter((control): control is ProjectRuntimeControl => control !== null);
}

function buildProjectRuntimeControl(
  source: RuntimeControlSource,
  input: BuildProjectRuntimeControlsInput,
): ProjectRuntimeControl | null {
  if (source.environmentId === null) return null;

  const environmentId = source.environmentId;
  const relayRecovery = buildRelayRecovery(environmentId, input);
  if (!hasRuntimeUtilityState(source, relayRecovery)) return null;

  return {
    key: environmentId,
    label: source.label,
    kind: source.kind,
    ...(source.status ? { status: source.status } : {}),
    disconnectAction: buildDisconnectAction(source.device, environmentId, input),
    relayRecovery,
  };
}

function hasRuntimeUtilityState(
  source: RuntimeControlSource,
  relayRecovery: ProjectRuntimeControl["relayRecovery"],
): boolean {
  return source.device !== null || source.status !== undefined || relayRecovery !== null;
}

function buildRelayRecovery(
  environmentId: EnvironmentId,
  input: BuildProjectRuntimeControlsInput,
): ProjectRuntimeControl["relayRecovery"] {
  const summary = input.relayRecoveryByRuntimeId[environmentId];
  if (summary === undefined || summary.state === "idle") return null;

  return {
    summary,
    onAcknowledge: () => input.acknowledgeRelayRecovery(environmentId),
  };
}

function buildDisconnectAction(
  device: CmmdDeviceRegistryDevice | null,
  environmentId: EnvironmentId,
  input: BuildProjectRuntimeControlsInput,
): ProjectRuntimeControl["disconnectAction"] {
  if (device?.authorization.bound !== true) return { state: "unavailable" };

  return {
    state: "available",
    isDisconnecting: input.disconnectingDeviceId === device.id,
    onDisconnect: () => {
      void input.disconnectDevice(device, environmentId);
    },
  };
}
